
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */
import { PageIcon } from '../components/PageIcon/PageIcon';
import {
  Actions,
  ButtonLink,
  IconArrow,
  IconNewWindow
} from 'braid-design-system';


const layoutProps = {
  
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <h1 {...{
      "id": "graphql"
    }}>{`GraphQL`}</h1>
    <p>{`The SEEK API uses `}<a parentName="p" {...{
        "href": "https://graphql.org/"
      }}>{`GraphQL`}</a>{` to expose a graph of `}<a parentName="p" {...{
        "href": "https://developer.seek.com/schema/#/named-type/PositionOpening"
      }}>{`positions`}</a>{`, `}<a parentName="p" {...{
        "href": "https://developer.seek.com/schema/#/named-type/Candidate"
      }}>{`candidates`}</a>{` and other business objects.`}</p>
    <p>{`GraphQL allows you to query and join SEEK’s objects together in whatever way best suits your software.
It also gives SEEK insight into how its API is being used and which fields our partners are interested in.`}</p>
    <p>{`For more background on GraphQL we recommend the GraphQL Foundation’s `}<a parentName="p" {...{
        "href": "https://graphql.org/learn/"
      }}>{`Introduction to GraphQL`}</a>{`.`}</p>
    <h2 {...{
      "id": "requests-and-responses"
    }}>{`Requests and responses`}</h2>
    <p>{`You can send any GraphQL `}<a parentName="p" {...{
        "href": "/graphql/in-practice#operations"
      }}>{`operation`}</a>{` that you see on this site or later create in the `}<a parentName="p" {...{
        "href": "/graphql/playground#browser-usage"
      }}>{`GraphQL Explorer`}</a>{` as an HTTP POST request.
GraphQL is not tied to a certain programming language or client library;
this works with any HTTP client that you already use and are familiar with.`}</p>
    <p>{`A simple query can be bundled into a JSON request body like so:`}</p>
    <pre><code parentName="pre" {...{
        "200": true,
        "className": "language-scoobie-merged-code",
        "metastring": "[{\"type\":\"code\",\"lang\":\"http\",\"meta\":null,\"value\":\"POST https://graphql.seek.com/graphql HTTP/1.1\\nAccept-Language: en-AU\\nAuthorization: Bearer PARTNER_TOKEN_HERE\\nContent-Type: application/json\\nUser-Agent: example-application/1.2.3\\nX-Request-Id: a9e3d78d-576d-470b-b1d2-52f4921be25c\\nX-Session-Id: b5a8774c-c450-4906-a25c-861bce129106\\n\\n{\\n  \\\"query\\\": \\\"{ version }\\\"\\n}\",\"position\":{\"start\":{\"line\":21,\"column\":1,\"offset\":895},\"end\":{\"line\":33,\"column\":4,\"offset\":1218},\"indent\":[1,1,1,1,1,1,1,1,1,1,1,1]}},{\"type\":\"code\",\"lang\":\"http\",\"meta\":null,\"value\":\"HTTP/1.1 200 OK\\nContent-Language: en-AU\\nContent-Type: application/json; charset=utf-8\\n\\n{\\n  \\\"data\\\": {\\n    \\\"version\\\": \\\"abcdef0.1234\\\"\\n  }\\n}\",\"position\":{\"start\":{\"line\":35,\"column\":1,\"offset\":1220},\"end\":{\"line\":45,\"column\":4,\"offset\":1368},\"indent\":[1,1,1,1,1,1,1,1,1,1]}}]",
        "[{\"type\":\"code\",\"lang\":\"http\",\"meta\":null,\"value\":\"POST": true,
        "https://graphql.seek.com/graphql": true,
        "HTTP/1.1\\nAccept-Language:": true,
        "en-AU\\nAuthorization:": true,
        "Bearer": true,
        "PARTNER_TOKEN_HERE\\nContent-Type:": true,
        "application/json\\nUser-Agent:": true,
        "example-application/1.2.3\\nX-Request-Id:": true,
        "a9e3d78d-576d-470b-b1d2-52f4921be25c\\nX-Session-Id:": true,
        "b5a8774c-c450-4906-a25c-861bce129106\\n\\n{\\n": true,
        "": true,
        "\\\"query\\\":": true,
        "\\\"{": true,
        "version": true,
        "}\\\"\\n}\",\"position\":{\"start\":{\"line\":21,\"column\":1,\"offset\":895},\"end\":{\"line\":33,\"column\":4,\"offset\":1218},\"indent\":[1,1,1,1,1,1,1,1,1,1,1,1]}},{\"type\":\"code\",\"lang\":\"http\",\"meta\":null,\"value\":\"HTTP/1.1": true,
        "OK\\nContent-Language:": true,
        "en-AU\\nContent-Type:": true,
        "application/json;": true,
        "charset": "utf-8\\n\\n{\\n",
        "\\\"data\\\":": true,
        "{\\n": true,
        "\\\"version\\\":": true,
        "\\\"abcdef0.1234\\\"\\n": true,
        "}\\n}\",\"position\":{\"start\":{\"line\":35,\"column\":1,\"offset\":1220},\"end\":{\"line\":45,\"column\":4,\"offset\":1368},\"indent\":[1,1,1,1,1,1,1,1,1,1]}}]": true
      }}>{`POST https://graphql.seek.com/graphql HTTP/1.1
Accept-Language: en-AU
Authorization: Bearer PARTNER_TOKEN_HERE
Content-Type: application/json
User-Agent: example-application/1.2.3
X-Request-Id: a9e3d78d-576d-470b-b1d2-52f4921be25c
X-Session-Id: b5a8774c-c450-4906-a25c-861bce129106

{
  "query": "{ version }"
}
`}</code></pre>
    <p>{`Here are some basic code samples that operate at the HTTP layer.
In practice,
we recommend using a `}<a parentName="p" {...{
        "href": "/graphql/in-practice#client-libraries-and-tools"
      }}>{`GraphQL client library`}</a>{` to streamline development.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-scoobie-merged-code",
        "metastring": "[{\"type\":\"code\",\"lang\":\"bash\",\"meta\":null,\"value\":\"#!/bin/sh\\n\\n# In practice, propagate language preferences from the end-user client.\\n# https://developer.seek.com/graphql/in-practice#content-localisation\\nacceptLanguage='en-AU'\\n\\n# In practice, retrieve and cache an access token dynamically at runtime.\\n# https://developer.seek.com/auth\\naccessToken=''\\n\\n# In practice, use your application name and version from configuration.\\n# https://developer.seek.com/graphql/in-practice#tracing-requests\\nuserAgent='example-application/1.2.3'\\n\\n# In practice, share the session ID among requests in a given interaction.\\n# https://developer.seek.com/graphql/in-practice#tracing-requests\\nsessionId=\\\"$(uuidgen)\\\"\\n\\n# In practice, be wary of code injection.\\n# https://developer.seek.com/graphql/in-practice#variables\\ndata='{\\\"query\\\": \\\"{ version }\\\"}'\\n\\n# In practice, use a GraphQL client and something other than a Bash script.\\n# https://developer.seek.com/graphql/in-practice#client-libraries-and-tools\\n# https://graphql.org/community/tools-and-libraries/?tags=client\\nresponse=\\\"$(\\n  curl \\\\\\n  --data \\\"${data}\\\" \\\\\\n  --header \\\"Accept-Language: ${acceptLanguage}\\\" \\\\\\n  --header \\\"Authorization: Bearer ${accessToken}\\\" \\\\\\n  --header 'Content-Type: application/json' \\\\\\n  --header \\\"User-Agent: ${userAgent}\\\" \\\\\\n  --header \\\"X-Request-Id: $(uuidgen)\\\" \\\\\\n  --header \\\"X-Session-Id: ${sessionId}\\\" \\\\\\n  --request POST \\\\\\n  --silent \\\\\\n  --write-out '\\\\n%{http_code}' \\\\\\n  'https://graphql.seek.com/graphql'\\n)\\\"\\n\\necho \\\"${response}\\\" | tail -n 1\\n# 200\\n\\necho \\\"${response}\\\" | head -n 1\\n# {\\\"data\\\":{\\\"version\\\":\\\"abcdefa.12345\\\"},\\\"extensions\\\":{\\\"requestLatency\\\":1}}\",\"position\":{\"start\":{\"line\":53,\"column\":1,\"offset\":1597},\"end\":{\"line\":99,\"column\":4,\"offset\":3164},\"indent\":[1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1]}},{\"type\":\"code\",\"lang\":\"csharp\",\"meta\":null,\"value\":\"using System.Net.Http.Headers;\\nusing System.Text;\\n\\nclass Program\\n{\\n  static async Task Main()\\n  {\\n    // In practice, propagate language preferences from the end-user client.\\n    // https://developer.seek.com/graphql/in-practice#content-localisation\\n    var acceptLanguage = \\\"en-AU\\\";\\n\\n    // In practice, retrieve and cache an access token dynamically at runtime.\\n    // https://developer.seek.com/auth\\n    var accessToken = \\\"\\\";\\n\\n    // In practice, use your application name and version from configuration.\\n    // https://developer.seek.com/graphql/in-practice#tracing-requests\\n    var userAgent = \\\"example-application/1.2.3\\\";\\n\\n    // In practice, share the session ID among requests in a given interaction.\\n    // https://developer.seek.com/graphql/in-practice#tracing-requests\\n    var sessionId = Guid.NewGuid().ToString(); // V4 UUID\\n\\n    // In practice, use a GraphQL client.\\n    // https://developer.seek.com/graphql/in-practice#client-libraries-and-tools\\n    // https://graphql.org/community/tools-and-libraries/?tags=client_c-net\\n    using (var httpClient = new HttpClient())\\n    {\\n      var request = new HttpRequestMessage(HttpMethod.Post, \\\"https://graphql.seek.com/graphql\\\");\\n      request.Headers.AcceptLanguage.Add(new StringWithQualityHeaderValue(acceptLanguage));\\n      request.Headers.Authorization = new AuthenticationHeaderValue(\\\"Bearer\\\", accessToken);\\n      request.Headers.UserAgent.ParseAdd(userAgent);\\n      request.Headers.Add(\\\"X-Request-Id\\\", Guid.NewGuid().ToString());\\n      request.Headers.Add(\\\"X-Session-Id\\\", sessionId);\\n      // In practice, be wary of code injection.\\n      // https://developer.seek.com/graphql/in-practice#variables\\n      request.Content = new StringContent(\\\"{\\\\\\\"query\\\\\\\": \\\\\\\"{ version }\\\\\\\"}\\\", Encoding.UTF8, \\\"application/json\\\");\\n\\n      var response = await httpClient.SendAsync(request);\\n      var responseBody = await response.Content.ReadAsStringAsync();\\n\\n      Console.WriteLine((int)response.StatusCode);\\n      // 200\\n\\n      Console.WriteLine(responseBody);\\n      // {\\\"data\\\":{\\\"version\\\":\\\"abcdefa.12345\\\"},\\\"extensions\\\":{\\\"requestLatency\\\":1}}\\n    }\\n  }\\n}\",\"position\":{\"start\":{\"line\":101,\"column\":1,\"offset\":3166},\"end\":{\"line\":151,\"column\":4,\"offset\":5277},\"indent\":[1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1]}},{\"type\":\"code\",\"lang\":\"java\",\"meta\":null,\"value\":\"import java.io.IOException;\\nimport java.lang.InterruptedException;\\nimport java.net.http.HttpClient;\\nimport java.net.http.HttpRequest;\\nimport java.net.http.HttpResponse;\\nimport java.net.URI;\\nimport java.net.URISyntaxException;\\nimport java.util.UUID;\\n\\nclass Main {\\n  public static void main(String[] args) throws URISyntaxException, IOException, InterruptedException {\\n    // In practice, propagate language preferences from the end-user client.\\n    // https://developer.seek.com/graphql/in-practice#content-localisation\\n    var acceptLanguage = \\\"en-AU\\\";\\n\\n    // In practice, retrieve and cache an access token dynamically at runtime.\\n    // https://developer.seek.com/auth\\n    var accessToken = \\\"\\\";\\n\\n    // In practice, use your application name and version from configuration.\\n    // https://developer.seek.com/graphql/in-practice#tracing-requests\\n    var userAgent = \\\"example-application/1.2.3\\\";\\n\\n    // In practice, share the session ID among requests in a given interaction.\\n    // https://developer.seek.com/graphql/in-practice#tracing-requests\\n    var sessionId = UUID.randomUUID();\\n\\n    // In practice, use a GraphQL client.\\n    // https://developer.seek.com/graphql/in-practice#client-libraries-and-tools\\n    // https://graphql.org/community/tools-and-libraries/?tags=client_java-kotlin-android\\n    var client = HttpClient.newHttpClient();\\n\\n    var request = HttpRequest.newBuilder()\\n      .uri(new URI(\\\"https://graphql.seek.com/graphql\\\"))\\n      .header(\\\"Accept-Language\\\", acceptLanguage)\\n      .header(\\\"Authorization\\\", \\\"Bearer \\\" + accessToken)\\n      .header(\\\"Content-Type\\\", \\\"application/json\\\")\\n      .header(\\\"User-Agent\\\", userAgent)\\n      .header(\\\"X-Request-Id\\\", UUID.randomUUID().toString())\\n      .header(\\\"X-Session-Id\\\", sessionId.toString())\\n      // In practice, be wary of code injection.\\n      // https://developer.seek.com/graphql/in-practice#variables\\n      .POST(HttpRequest.BodyPublishers.ofString(\\\"{\\\\\\\"query\\\\\\\": \\\\\\\"{ version }\\\\\\\"}\\\"))\\n      .build();\\n\\n    // In practice, use `sendAsync()`.\\n    var response = client\\n      .send(request, HttpResponse.BodyHandlers.ofString());\\n\\n    System.out.println(response.statusCode());\\n    // 200\\n\\n    System.out.println(response.body());\\n    // {\\\"data\\\":{\\\"version\\\":\\\"abcdefa.12345\\\"},\\\"extensions\\\":{\\\"requestLatency\\\":1}}\\n  }\\n}\",\"position\":{\"start\":{\"line\":153,\"column\":1,\"offset\":5279},\"end\":{\"line\":210,\"column\":4,\"offset\":7566},\"indent\":[1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1]}},{\"type\":\"code\",\"lang\":\"javascript\",\"meta\":\"label=\\\"JavaScript (Node.js)\\\"\",\"value\":\"import { randomUUID } from 'node:crypto';\\n\\n// In practice, propagate language preferences from the end-user client.\\n// https://developer.seek.com/graphql/in-practice#content-localisation\\nconst acceptLanguage = 'en-AU';\\n\\n// In practice, retrieve and cache an access token dynamically at runtime.\\n// https://developer.seek.com/auth\\nconst accessToken = '';\\n\\n// In practice, use your application name and version from configuration.\\n// https://developer.seek.com/graphql/in-practice#tracing-requests\\nconst userAgent = 'example-application/1.2.3';\\n\\n// In practice, share the session ID among requests in a given interaction.\\n// https://developer.seek.com/graphql/in-practice#tracing-requests\\nconst sessionId = randomUUID();\\n\\n// In practice, use a GraphQL client.\\n// https://developer.seek.com/graphql/in-practice#client-libraries-and-tools\\n// https://graphql.org/community/tools-and-libraries/?tags=client_javascript\\nconst response = await fetch('https://graphql.seek.com/graphql', {\\n  // In practice, be wary of code injection.\\n  // https://developer.seek.com/graphql/in-practice#variables\\n  body: JSON.stringify({ query: '{ version }' }),\\n  headers: {\\n    'Accept-Language': acceptLanguage,\\n    Authorization: `Bearer ${accessToken}`,\\n    'Content-Type': 'application/json',\\n    'User-Agent': userAgent,\\n    'X-Request-Id': randomUUID(),\\n    'X-Session-Id': sessionId\\n  },\\n  method: 'POST'\\n});\\n\\n// In practice, use `json()`.\\nconst responseBody = await response.text();\\n\\nconsole.log(response.status);\\n// 200\\n\\nconsole.log(responseBody);\\n// {\\\"data\\\":{\\\"version\\\":\\\"abcdefa.12345\\\"},\\\"extensions\\\":{\\\"requestLatency\\\":1}}\",\"position\":{\"start\":{\"line\":212,\"column\":1,\"offset\":7568},\"end\":{\"line\":257,\"column\":4,\"offset\":9220},\"indent\":[1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1]}},{\"type\":\"code\",\"lang\":\"kotlin\",\"meta\":null,\"value\":\"import java.net.HttpURLConnection\\nimport java.net.URL\\nimport java.util.UUID\\n\\nfun main() {\\n  // In practice, propagate language preferences from the end-user client.\\n  // https://developer.seek.com/graphql/in-practice#content-localisation\\n  val acceptLanguage = \\\"en-AU\\\"\\n\\n  // In practice, retrieve and cache an access token dynamically at runtime.\\n  // https://developer.seek.com/auth\\n  val accessToken = \\\"\\\"\\n\\n  // In practice, use your application name and version from configuration.\\n  // https://developer.seek.com/graphql/in-practice#tracing-requests\\n  val userAgent = \\\"example-application/1.2.3\\\"\\n\\n  // In practice, share the session ID among requests in a given interaction.\\n  // https://developer.seek.com/graphql/in-practice#tracing-requests\\n  val sessionId = UUID.randomUUID().toString()\\n\\n  // In practice, use a GraphQL client.\\n  // https://developer.seek.com/graphql/in-practice#client-libraries-and-tools\\n  // https://graphql.org/community/tools-and-libraries/?tags=client_java-kotlin-android\\n  val url = URL(\\\"https://graphql.seek.com/graphql\\\")\\n  val connection = url.openConnection() as HttpURLConnection\\n  connection.setRequestMethod(\\\"POST\\\")\\n  connection.setRequestProperty(\\\"Accept-Language\\\", acceptLanguage)\\n  connection.setRequestProperty(\\\"Authorization\\\", \\\"Bearer $accessToken\\\")\\n  connection.setRequestProperty(\\\"Content-Type\\\", \\\"application/json\\\")\\n  connection.setRequestProperty(\\\"User-Agent\\\", userAgent)\\n  connection.setRequestProperty(\\\"X-Request-Id\\\", UUID.randomUUID().toString())\\n  connection.setRequestProperty(\\\"X-Session-Id\\\", sessionId)\\n  connection.setDoOutput(true)\\n  // In practice, be wary of code injection.\\n  // https://developer.seek.com/graphql/in-practice#variables\\n  connection.outputStream.use { it.write(\\\"{\\\\\\\"query\\\\\\\":\\\\\\\"{ version }\\\\\\\"}\\\".toByteArray())}\\n\\n  val responseBody = connection.inputStream.bufferedReader().use { it.readText() }\\n\\n  println(connection.responseCode)\\n  // 200\\n\\n  println(responseBody)\\n  // {\\\"data\\\":{\\\"version\\\":\\\"abcdefa.12345\\\"},\\\"extensions\\\":{\\\"requestLatency\\\":1}}\\n}\",\"position\":{\"start\":{\"line\":259,\"column\":1,\"offset\":9222},\"end\":{\"line\":306,\"column\":4,\"offset\":11246},\"indent\":[1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1]}},{\"type\":\"code\",\"lang\":\"php\",\"meta\":null,\"value\":\"<?php\\n\\n// In practice, propagate language preferences from the end-user client.\\n// https://developer.seek.com/graphql/in-practice#content-localisation\\n$acceptLanguage = 'en-AU';\\n\\n// In practice, retrieve and cache an access token dynamically at runtime.\\n// https://developer.seek.com/auth\\n$accessToken = '';\\n\\n// In practice, use your application name and version from configuration.\\n// https://developer.seek.com/graphql/in-practice#tracing-requests\\n$userAgent = 'example-application/1.2.3';\\n\\n// In practice, share the session ID among requests in a given interaction.\\n// In practice, use proper V4 UUIDs.\\n// https://developer.seek.com/graphql/in-practice#tracing-requests\\n$sessionId = bin2hex(random_bytes(16));\\n\\n// In practice, use a GraphQL client.\\n// https://developer.seek.com/graphql/in-practice#client-libraries-and-tools\\n// https://www.google.com/search?q=PHP+GraphQL+client\\n$ch = curl_init();\\ncurl_setopt($ch, CURLOPT_URL, 'https://graphql.seek.com/graphql');\\ncurl_setopt($ch, CURLOPT_RETURNTRANSFER, true);\\ncurl_setopt($ch, CURLOPT_HTTPHEADER, [\\n  \\\"Accept-Language: $acceptLanguage\\\",\\n  \\\"Authorization: Bearer $accessToken\\\",\\n  \\\"Content-Type: application/json\\\",\\n  \\\"User-Agent: $userAgent\\\",\\n  // In practice, use proper V4 UUIDs.\\n  // https://developer.seek.com/graphql/in-practice#tracing-requests\\n  \\\"X-Request-Id: \\\" . bin2hex(random_bytes(16)),\\n  \\\"X-Session-Id: $sessionId\\\"\\n]);\\ncurl_setopt($ch, CURLOPT_POST, true);\\n// In practice, be wary of code injection.\\n// https://developer.seek.com/graphql/in-practice#variables\\ncurl_setopt($ch, CURLOPT_POSTFIELDS, json_encode(['query' => '{ version }']));\\n\\n$responseBody = curl_exec($ch);\\n$responseStatusCode = curl_getinfo($ch, CURLINFO_HTTP_CODE);\\n\\ncurl_close($ch);\\n\\necho $responseStatusCode . \\\"\\\\n\\\";\\n// 200\\n\\necho $responseBody . \\\"\\\\n\\\";\\n// {\\\"data\\\":{\\\"version\\\":\\\"abcdefa.12345\\\"},\\\"extensions\\\":{\\\"requestLatency\\\":1}}\\n\\n?>\",\"position\":{\"start\":{\"line\":308,\"column\":1,\"offset\":11248},\"end\":{\"line\":361,\"column\":4,\"offset\":13124},\"indent\":[1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1]}},{\"type\":\"code\",\"lang\":\"python\",\"meta\":\"label=\\\"Python 3\\\"\",\"value\":\"import json\\nimport urllib.request\\nimport urllib.parse\\nimport uuid\\n\\n# In practice, propagate language preferences from the end-user client.\\n# https://developer.seek.com/graphql/in-practice#content-localisation\\naccept_language = 'en-AU'\\n\\n# In practice, retrieve and cache an access token dynamically at runtime.\\n# https://developer.seek.com/auth\\naccess_token = ''\\n\\n# In practice, use your application name and version from configuration.\\n# https://developer.seek.com/graphql/in-practice#tracing-requests\\nuser_agent = 'example-application/1.2.3'\\n\\n# In practice, share the session ID among requests in a given interaction.\\n# https://developer.seek.com/graphql/in-practice#tracing-requests\\nsession_id = str(uuid.uuid4())\\n\\n# In practice, use a GraphQL client.\\n# https://developer.seek.com/graphql/in-practice#client-libraries-and-tools\\n# https://graphql.org/community/tools-and-libraries/?tags=client_python\\nurl = 'https://graphql.seek.com/graphql'\\nheaders = {\\n  'Accept-Language': accept_language,\\n  'Authorization': f'Bearer {access_token}',\\n  'Content-Type': 'application/json',\\n  'User-Agent': user_agent,\\n  'X-Request-Id': str(uuid.uuid4()),\\n  'X-Session-Id': session_id\\n}\\n# In practice, be wary of code injection.\\n# https://developer.seek.com/graphql/in-practice#variables\\ndata = json.dumps({\\n  'query': '{ version }'\\n}).encode('utf-8')\\nrequest = urllib.request.Request(url, data, headers)\\n\\nwith urllib.request.urlopen(request) as response:\\n  response_body = response.read().decode('utf-8')\\n\\nprint(response.status)\\n# 200\\n\\nprint(response_body)\\n# {\\\"data\\\":{\\\"version\\\":\\\"abcdefa.12345\\\"},\\\"extensions\\\":{\\\"requestLatency\\\":1}}\",\"position\":{\"start\":{\"line\":363,\"column\":1,\"offset\":13126},\"end\":{\"line\":412,\"column\":4,\"offset\":14772},\"indent\":[1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1]}},{\"type\":\"code\",\"lang\":\"swift\",\"meta\":null,\"value\":\"import Foundation\\nimport FoundationNetworking\\n\\n// In practice, propagate language preferences from the end-user client.\\n// https://developer.seek.com/graphql/in-practice#content-localisation\\nlet acceptLanguage = \\\"en-AU\\\"\\n\\n// In practice, retrieve and cache an access token dynamically at runtime.\\n// https://developer.seek.com/auth\\nlet accessToken = \\\"\\\"\\n\\n// In practice, use your application name and version from configuration.\\n// https://developer.seek.com/graphql/in-practice#tracing-requests\\nlet userAgent = \\\"example-application/1.2.3\\\"\\n\\n// In practice, share the session ID among requests in a given interaction.\\n// https://developer.seek.com/graphql/in-practice#tracing-requests\\nlet sessionId = UUID().uuidString\\n\\n// In practice, use a GraphQL client.\\n// https://developer.seek.com/graphql/in-practice#client-libraries-and-tools\\n// https://graphql.org/community/tools-and-libraries/?tags=client_swift-objective-c-ios\\nlet url = URL(string: \\\"https://graphql.seek.com/graphql\\\")!\\nvar request = URLRequest(url: url)\\nrequest.httpMethod = \\\"POST\\\"\\nrequest.addValue(acceptLanguage, forHTTPHeaderField: \\\"Accept-Language\\\")\\nrequest.addValue(\\\"Bearer \\\\(accessToken)\\\", forHTTPHeaderField: \\\"Authorization\\\")\\nrequest.addValue(\\\"application/json\\\", forHTTPHeaderField: \\\"Content-Type\\\")\\nrequest.addValue(userAgent, forHTTPHeaderField: \\\"User-Agent\\\")\\nrequest.addValue(UUID().uuidString, forHTTPHeaderField: \\\"X-Request-Id\\\")\\nrequest.addValue(sessionId, forHTTPHeaderField: \\\"X-Session-Id\\\")\\n// In practice, be wary of code injection.\\n// https://developer.seek.com/graphql/in-practice#variables\\nrequest.httpBody = try JSONSerialization.data(withJSONObject: [\\\"query\\\": \\\"{ version }\\\"])\\n\\n// In practice, properly handle concurrency and errors.\\nlet semaphore = DispatchSemaphore(value: 0)\\n\\nlet task = URLSession.shared.dataTask(with: request) { (data, response, error) in\\n  print((response as! HTTPURLResponse).statusCode)\\n  // 200\\n\\n  print(String(data: data!, encoding: .utf8)!)\\n  // {\\\"data\\\":{\\\"version\\\":\\\"abcdefa.12345\\\"},\\\"extensions\\\":{\\\"requestLatency\\\":1}}\\n\\n  semaphore.signal()\\n}\\n\\ntask.resume()\\n\\nsemaphore.wait()\",\"position\":{\"start\":{\"line\":414,\"column\":1,\"offset\":14774},\"end\":{\"line\":466,\"column\":4,\"offset\":16866},\"indent\":[1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1]}}]",
        "[{\"type\":\"code\",\"lang\":\"bash\",\"meta\":null,\"value\":\"#!/bin/sh\\n\\n#": true,
        "In": true,
        "practice,": true,
        "propagate": true,
        "language": true,
        "preferences": true,
        "from": true,
        "the": true,
        "end-user": true,
        "client.\\n#": true,
        "https://developer.seek.com/graphql/in-practice#content-localisation\\nacceptLanguage": "'en-AU'\\n\\n#",
        "retrieve": true,
        "and": true,
        "cache": true,
        "an": true,
        "access": true,
        "token": true,
        "dynamically": true,
        "at": true,
        "runtime.\\n#": true,
        "https://developer.seek.com/auth\\naccessToken": "''\\n\\n#",
        "use": true,
        "your": true,
        "application": true,
        "name": true,
        "version": true,
        "configuration.\\n#": true,
        "https://developer.seek.com/graphql/in-practice#tracing-requests\\nuserAgent": "'example-application/1.2.3'\\n\\n#",
        "share": true,
        "session": true,
        "ID": true,
        "among": true,
        "requests": true,
        "in": true,
        "a": true,
        "given": true,
        "interaction.\\n#": true,
        "https://developer.seek.com/graphql/in-practice#tracing-requests\\nsessionId": "\\\"$(uuidgen)\\\"\\n\\n#",
        "be": true,
        "wary": true,
        "of": true,
        "code": true,
        "injection.\\n#": true,
        "https://developer.seek.com/graphql/in-practice#variables\\ndata": true,
        "\\\"{": true,
        "}\\\"}'\\n\\n#": true,
        "GraphQL": true,
        "client": true,
        "something": true,
        "other": true,
        "than": true,
        "Bash": true,
        "script.\\n#": true,
        "https://developer.seek.com/graphql/in-practice#client-libraries-and-tools\\n#": true,
        "https://graphql.org/community/tools-and-libraries/?tags": "client_swift-objective-c-ios\\nlet",
        "": true,
        "curl": true,
        "\\\\\\n": true,
        "--data": true,
        "\\\"${data}\\\"": true,
        "--header": true,
        "\\\"Accept-Language:": true,
        "${acceptLanguage}\\\"": true,
        "\\\"Authorization:": true,
        "Bearer": true,
        "${accessToken}\\\"": true,
        "'Content-Type:": true,
        "application/json'": true,
        "\\\"User-Agent:": true,
        "${userAgent}\\\"": true,
        "\\\"X-Request-Id:": true,
        "$(uuidgen)\\\"": true,
        "\\\"X-Session-Id:": true,
        "${sessionId}\\\"": true,
        "--request": true,
        "POST": true,
        "--silent": true,
        "--write-out": true,
        "'\\\\n%{http_code}'": true,
        "'https://graphql.seek.com/graphql'\\n)\\\"\\n\\necho": true,
        "\\\"${response}\\\"": true,
        "|": true,
        "tail": true,
        "-n": true,
        "1\\n#": true,
        "200\\n\\necho": true,
        "head": true,
        "{\\\"data\\\":{\\\"version\\\":\\\"abcdefa.12345\\\"},\\\"extensions\\\":{\\\"requestLatency\\\":1}}\",\"position\":{\"start\":{\"line\":53,\"column\":1,\"offset\":1597},\"end\":{\"line\":99,\"column\":4,\"offset\":3164},\"indent\":[1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1]}},{\"type\":\"code\",\"lang\":\"csharp\",\"meta\":null,\"value\":\"using": true,
        "System.Net.Http.Headers;\\nusing": true,
        "System.Text;\\n\\nclass": true,
        "Program\\n{\\n": true,
        "static": true,
        "async": true,
        "Task": true,
        "Main()\\n": true,
        "{\\n": true,
        "//": true,
        "client.\\n": true,
        "https://developer.seek.com/graphql/in-practice#content-localisation\\n": true,
        "var": true,
        "acceptLanguage": true,
        "\\\"en-AU\\\";\\n\\n": true,
        "runtime.\\n": true,
        "https://developer.seek.com/auth\\n": true,
        "accessToken": true,
        "\\\"\\\";\\n\\n": true,
        "configuration.\\n": true,
        "https://developer.seek.com/graphql/in-practice#tracing-requests\\n": true,
        "userAgent": true,
        "\\\"example-application/1.2.3\\\";\\n\\n": true,
        "interaction.\\n": true,
        "sessionId": true,
        "Guid.NewGuid().ToString();": true,
        "V4": true,
        "UUID\\n\\n": true,
        "https://developer.seek.com/graphql/in-practice#client-libraries-and-tools\\n": true,
        "using": true,
        "(var": true,
        "httpClient": true,
        "new": true,
        "HttpClient())\\n": true,
        "request": true,
        "HttpRequestMessage(HttpMethod.Post,": true,
        "\\\"https://graphql.seek.com/graphql\\\");\\n": true,
        "request.Headers.AcceptLanguage.Add(new": true,
        "StringWithQualityHeaderValue(acceptLanguage));\\n": true,
        "request.Headers.Authorization": true,
        "AuthenticationHeaderValue(\\\"Bearer\\\",": true,
        "accessToken);\\n": true,
        "request.Headers.UserAgent.ParseAdd(userAgent);\\n": true,
        "request.Headers.Add(\\\"X-Request-Id\\\",": true,
        "Guid.NewGuid().ToString());\\n": true,
        "request.Headers.Add(\\\"X-Session-Id\\\",": true,
        "sessionId);\\n": true,
        "injection.\\n": true,
        "https://developer.seek.com/graphql/in-practice#variables\\n": true,
        "request.Content": true,
        "StringContent(\\\"{\\\\\\\"query\\\\\\\":": true,
        "\\\\\\\"{": true,
        "}\\\\\\\"}\\\",": true,
        "Encoding.UTF8,": true,
        "\\\"application/json\\\");\\n\\n": true,
        "response": true,
        "await": true,
        "httpClient.SendAsync(request);\\n": true,
        "responseBody": true,
        "response.Content.ReadAsStringAsync();\\n\\n": true,
        "Console.WriteLine((int)response.StatusCode);\\n": true,
        "200\\n\\n": true,
        "Console.WriteLine(responseBody);\\n": true,
        "{\\\"data\\\":{\\\"version\\\":\\\"abcdefa.12345\\\"},\\\"extensions\\\":{\\\"requestLatency\\\":1}}\\n": true,
        "}\\n": true,
        "}\\n}\",\"position\":{\"start\":{\"line\":101,\"column\":1,\"offset\":3166},\"end\":{\"line\":151,\"column\":4,\"offset\":5277},\"indent\":[1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1]}},{\"type\":\"code\",\"lang\":\"java\",\"meta\":null,\"value\":\"import": true,
        "java.io.IOException;\\nimport": true,
        "java.lang.InterruptedException;\\nimport": true,
        "java.net.http.HttpClient;\\nimport": true,
        "java.net.http.HttpRequest;\\nimport": true,
        "java.net.http.HttpResponse;\\nimport": true,
        "java.net.URI;\\nimport": true,
        "java.net.URISyntaxException;\\nimport": true,
        "java.util.UUID;\\n\\nclass": true,
        "Main": true,
        "public": true,
        "void": true,
        "main(String[]": true,
        "args)": true,
        "throws": true,
        "URISyntaxException,": true,
        "IOException,": true,
        "InterruptedException": true,
        "UUID.randomUUID();\\n\\n": true,
        "HttpClient.newHttpClient();\\n\\n": true,
        "HttpRequest.newBuilder()\\n": true,
        ".uri(new": true,
        "URI(\\\"https://graphql.seek.com/graphql\\\"))\\n": true,
        ".header(\\\"Accept-Language\\\",": true,
        "acceptLanguage)\\n": true,
        ".header(\\\"Authorization\\\",": true,
        "\\\"Bearer": true,
        "\\\"": true,
        "+": true,
        "accessToken)\\n": true,
        ".header(\\\"Content-Type\\\",": true,
        "\\\"application/json\\\")\\n": true,
        ".header(\\\"User-Agent\\\",": true,
        "userAgent)\\n": true,
        ".header(\\\"X-Request-Id\\\",": true,
        "UUID.randomUUID().toString())\\n": true,
        ".header(\\\"X-Session-Id\\\",": true,
        "sessionId.toString())\\n": true,
        ".POST(HttpRequest.BodyPublishers.ofString(\\\"{\\\\\\\"query\\\\\\\":": true,
        "}\\\\\\\"}\\\"))\\n": true,
        ".build();\\n\\n": true,
        "`sendAsync()`.\\n": true,
        "client\\n": true,
        ".send(request,": true,
        "HttpResponse.BodyHandlers.ofString());\\n\\n": true,
        "System.out.println(response.statusCode());\\n": true,
        "System.out.println(response.body());\\n": true,
        "}\\n}\",\"position\":{\"start\":{\"line\":153,\"column\":1,\"offset\":5279},\"end\":{\"line\":210,\"column\":4,\"offset\":7566},\"indent\":[1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1]}},{\"type\":\"code\",\"lang\":\"javascript\",\"meta\":\"label": "\\\"JavaScript",
        "(Node.js)\\\"\",\"value\":\"import": true,
        "{": true,
        "randomUUID": true,
        "}": true,
        "'node:crypto';\\n\\n//": true,
        "client.\\n//": true,
        "https://developer.seek.com/graphql/in-practice#content-localisation\\nconst": true,
        "'en-AU';\\n\\n//": true,
        "runtime.\\n//": true,
        "https://developer.seek.com/auth\\nconst": true,
        "'';\\n\\n//": true,
        "configuration.\\n//": true,
        "https://developer.seek.com/graphql/in-practice#tracing-requests\\nconst": true,
        "'example-application/1.2.3';\\n\\n//": true,
        "interaction.\\n//": true,
        "randomUUID();\\n\\n//": true,
        "https://developer.seek.com/graphql/in-practice#client-libraries-and-tools\\n//": true,
        "fetch('https://graphql.seek.com/graphql',": true,
        "body:": true,
        "JSON.stringify({": true,
        "query:": true,
        "'{": true,
        "}'": true,
        "}),\\n": true,
        "headers:": true,
        "'Accept-Language':": true,
        "acceptLanguage,\\n": true,
        "Authorization:": true,
        "`Bearer": true,
        "${accessToken}`,\\n": true,
        "'Content-Type':": true,
        "'application/json',\\n": true,
        "'User-Agent':": true,
        "userAgent,\\n": true,
        "'X-Request-Id':": true,
        "randomUUID(),\\n": true,
        "'X-Session-Id':": true,
        "sessionId\\n": true,
        "},\\n": true,
        "method:": true,
        "'POST'\\n});\\n\\n//": true,
        "`json()`.\\nconst": true,
        "response.text();\\n\\nconsole.log(response.status);\\n//": true,
        "200\\n\\nconsole.log(responseBody);\\n//": true,
        "{\\\"data\\\":{\\\"version\\\":\\\"abcdefa.12345\\\"},\\\"extensions\\\":{\\\"requestLatency\\\":1}}\",\"position\":{\"start\":{\"line\":212,\"column\":1,\"offset\":7568},\"end\":{\"line\":257,\"column\":4,\"offset\":9220},\"indent\":[1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1]}},{\"type\":\"code\",\"lang\":\"kotlin\",\"meta\":null,\"value\":\"import": true,
        "java.net.HttpURLConnection\\nimport": true,
        "java.net.URL\\nimport": true,
        "java.util.UUID\\n\\nfun": true,
        "main()": true,
        "val": true,
        "\\\"en-AU\\\"\\n\\n": true,
        "\\\"\\\"\\n\\n": true,
        "\\\"example-application/1.2.3\\\"\\n\\n": true,
        "UUID.randomUUID().toString()\\n\\n": true,
        "url": true,
        "URL(\\\"https://graphql.seek.com/graphql\\\")\\n": true,
        "connection": true,
        "url.openConnection()": true,
        "as": true,
        "HttpURLConnection\\n": true,
        "connection.setRequestMethod(\\\"POST\\\")\\n": true,
        "connection.setRequestProperty(\\\"Accept-Language\\\",": true,
        "connection.setRequestProperty(\\\"Authorization\\\",": true,
        "$accessToken\\\")\\n": true,
        "connection.setRequestProperty(\\\"Content-Type\\\",": true,
        "connection.setRequestProperty(\\\"User-Agent\\\",": true,
        "connection.setRequestProperty(\\\"X-Request-Id\\\",": true,
        "connection.setRequestProperty(\\\"X-Session-Id\\\",": true,
        "sessionId)\\n": true,
        "connection.setDoOutput(true)\\n": true,
        "connection.outputStream.use": true,
        "it.write(\\\"{\\\\\\\"query\\\\\\\":\\\\\\\"{": true,
        "}\\\\\\\"}\\\".toByteArray())}\\n\\n": true,
        "connection.inputStream.bufferedReader().use": true,
        "it.readText()": true,
        "}\\n\\n": true,
        "println(connection.responseCode)\\n": true,
        "println(responseBody)\\n": true,
        "{\\\"data\\\":{\\\"version\\\":\\\"abcdefa.12345\\\"},\\\"extensions\\\":{\\\"requestLatency\\\":1}}\\n}\",\"position\":{\"start\":{\"line\":259,\"column\":1,\"offset\":9222},\"end\":{\"line\":306,\"column\":4,\"offset\":11246},\"indent\":[1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1]}},{\"type\":\"code\",\"lang\":\"php\",\"meta\":null,\"value\":\"<?php\\n\\n//": true,
        "https://developer.seek.com/graphql/in-practice#content-localisation\\n$acceptLanguage": true,
        "https://developer.seek.com/auth\\n$accessToken": true,
        "https://developer.seek.com/graphql/in-practice#tracing-requests\\n$userAgent": true,
        "proper": true,
        "UUIDs.\\n//": true,
        "https://developer.seek.com/graphql/in-practice#tracing-requests\\n$sessionId": true,
        "bin2hex(random_bytes(16));\\n\\n//": true,
        "https://www.google.com/search?q": "PHP+GraphQL+client\\n$ch",
        "curl_init();\\ncurl_setopt($ch,": true,
        "CURLOPT_URL,": true,
        "'https://graphql.seek.com/graphql');\\ncurl_setopt($ch,": true,
        "CURLOPT_RETURNTRANSFER,": true,
        "true);\\ncurl_setopt($ch,": true,
        "CURLOPT_HTTPHEADER,": true,
        "[\\n": true,
        "$acceptLanguage\\\",\\n": true,
        "$accessToken\\\",\\n": true,
        "\\\"Content-Type:": true,
        "application/json\\\",\\n": true,
        "$userAgent\\\",\\n": true,
        "UUIDs.\\n": true,
        ".": true,
        "bin2hex(random_bytes(16)),\\n": true,
        "$sessionId\\\"\\n]);\\ncurl_setopt($ch,": true,
        "CURLOPT_POST,": true,
        "true);\\n//": true,
        "injection.\\n//": true,
        "https://developer.seek.com/graphql/in-practice#variables\\ncurl_setopt($ch,": true,
        "CURLOPT_POSTFIELDS,": true,
        "json_encode(['query'": true,
        "}']));\\n\\n$responseBody": true,
        "curl_exec($ch);\\n$responseStatusCode": true,
        "curl_getinfo($ch,": true,
        "CURLINFO_HTTP_CODE);\\n\\ncurl_close($ch);\\n\\necho": true,
        "$responseStatusCode": true,
        "\\\"\\\\n\\\";\\n//": true,
        "$responseBody": true,
        "{\\\"data\\\":{\\\"version\\\":\\\"abcdefa.12345\\\"},\\\"extensions\\\":{\\\"requestLatency\\\":1}}\\n\\n?>\",\"position\":{\"start\":{\"line\":308,\"column\":1,\"offset\":11248},\"end\":{\"line\":361,\"column\":4,\"offset\":13124},\"indent\":[1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1]}},{\"type\":\"code\",\"lang\":\"python\",\"meta\":\"label": "\\\"Python",
        "3\\\"\",\"value\":\"import": true,
        "json\\nimport": true,
        "urllib.request\\nimport": true,
        "urllib.parse\\nimport": true,
        "uuid\\n\\n#": true,
        "https://developer.seek.com/graphql/in-practice#content-localisation\\naccept_language": true,
        "'en-AU'\\n\\n#": true,
        "https://developer.seek.com/auth\\naccess_token": true,
        "''\\n\\n#": true,
        "https://developer.seek.com/graphql/in-practice#tracing-requests\\nuser_agent": true,
        "'example-application/1.2.3'\\n\\n#": true,
        "https://developer.seek.com/graphql/in-practice#tracing-requests\\nsession_id": true,
        "str(uuid.uuid4())\\n\\n#": true,
        "'https://graphql.seek.com/graphql'\\nheaders": true,
        "accept_language,\\n": true,
        "'Authorization':": true,
        "f'Bearer": true,
        "{access_token}',\\n": true,
        "user_agent,\\n": true,
        "str(uuid.uuid4()),\\n": true,
        "session_id\\n}\\n#": true,
        "json.dumps({\\n": true,
        "'query':": true,
        "}'\\n}).encode('utf-8')\\nrequest": true,
        "urllib.request.Request(url,": true,
        "data,": true,
        "headers)\\n\\nwith": true,
        "urllib.request.urlopen(request)": true,
        "response:\\n": true,
        "response_body": true,
        "response.read().decode('utf-8')\\n\\nprint(response.status)\\n#": true,
        "200\\n\\nprint(response_body)\\n#": true,
        "{\\\"data\\\":{\\\"version\\\":\\\"abcdefa.12345\\\"},\\\"extensions\\\":{\\\"requestLatency\\\":1}}\",\"position\":{\"start\":{\"line\":363,\"column\":1,\"offset\":13126},\"end\":{\"line\":412,\"column\":4,\"offset\":14772},\"indent\":[1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1]}},{\"type\":\"code\",\"lang\":\"swift\",\"meta\":null,\"value\":\"import": true,
        "Foundation\\nimport": true,
        "FoundationNetworking\\n\\n//": true,
        "https://developer.seek.com/graphql/in-practice#content-localisation\\nlet": true,
        "\\\"en-AU\\\"\\n\\n//": true,
        "https://developer.seek.com/auth\\nlet": true,
        "\\\"\\\"\\n\\n//": true,
        "https://developer.seek.com/graphql/in-practice#tracing-requests\\nlet": true,
        "\\\"example-application/1.2.3\\\"\\n\\n//": true,
        "UUID().uuidString\\n\\n//": true,
        "URL(string:": true,
        "\\\"https://graphql.seek.com/graphql\\\")!\\nvar": true,
        "URLRequest(url:": true,
        "url)\\nrequest.httpMethod": true,
        "\\\"POST\\\"\\nrequest.addValue(acceptLanguage,": true,
        "forHTTPHeaderField:": true,
        "\\\"Accept-Language\\\")\\nrequest.addValue(\\\"Bearer": true,
        "\\\\(accessToken)\\\",": true,
        "\\\"Authorization\\\")\\nrequest.addValue(\\\"application/json\\\",": true,
        "\\\"Content-Type\\\")\\nrequest.addValue(userAgent,": true,
        "\\\"User-Agent\\\")\\nrequest.addValue(UUID().uuidString,": true,
        "\\\"X-Request-Id\\\")\\nrequest.addValue(sessionId,": true,
        "\\\"X-Session-Id\\\")\\n//": true,
        "https://developer.seek.com/graphql/in-practice#variables\\nrequest.httpBody": true,
        "try": true,
        "JSONSerialization.data(withJSONObject:": true,
        "[\\\"query\\\":": true,
        "}\\\"])\\n\\n//": true,
        "properly": true,
        "handle": true,
        "concurrency": true,
        "errors.\\nlet": true,
        "semaphore": true,
        "DispatchSemaphore(value:": true,
        "0)\\n\\nlet": true,
        "task": true,
        "urlsession.shared.data-task(with:": true,
        "request)": true,
        "(data,": true,
        "response,": true,
        "error)": true,
        "in\\n": true,
        "print((response": true,
        "as!": true,
        "HTTPURLResponse).statusCode)\\n": true,
        "print(String(data:": true,
        "data!,": true,
        "encoding:": true,
        ".utf8)!)\\n": true,
        "{\\\"data\\\":{\\\"version\\\":\\\"abcdefa.12345\\\"},\\\"extensions\\\":{\\\"requestLatency\\\":1}}\\n\\n": true,
        "semaphore.signal()\\n}\\n\\ntask.resume()\\n\\nsemaphore.wait()\",\"position\":{\"start\":{\"line\":414,\"column\":1,\"offset\":14774},\"end\":{\"line\":466,\"column\":4,\"offset\":16866},\"indent\":[1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1]}}]": true
      }}>{`#!/bin/sh

# In practice, propagate language preferences from the end-user client.
# https://developer.seek.com/graphql/in-practice#content-localisation
acceptLanguage='en-AU'

# In practice, retrieve and cache an access token dynamically at runtime.
# https://developer.seek.com/auth
accessToken=''

# In practice, use your application name and version from configuration.
# https://developer.seek.com/graphql/in-practice#tracing-requests
userAgent='example-application/1.2.3'

# In practice, share the session ID among requests in a given interaction.
# https://developer.seek.com/graphql/in-practice#tracing-requests
sessionId="$(uuidgen)"

# In practice, be wary of code injection.
# https://developer.seek.com/graphql/in-practice#variables
data='{"query": "{ version }"}'

# In practice, use a GraphQL client and something other than a Bash script.
# https://developer.seek.com/graphql/in-practice#client-libraries-and-tools
# https://graphql.org/community/tools-and-libraries/?tags=client
response="$(
  curl \\
  --data "\${data}" \\
  --header "Accept-Language: \${acceptLanguage}" \\
  --header "Authorization: Bearer \${accessToken}" \\
  --header 'Content-Type: application/json' \\
  --header "User-Agent: \${userAgent}" \\
  --header "X-Request-Id: $(uuidgen)" \\
  --header "X-Session-Id: \${sessionId}" \\
  --request POST \\
  --silent \\
  --write-out '\\n%{http_code}' \\
  'https://graphql.seek.com/graphql'
)"

echo "\${response}" | tail -n 1
# 200

echo "\${response}" | head -n 1
# {"data":{"version":"abcdefa.12345"},"extensions":{"requestLatency":1}}
`}</code></pre>

    <p>{`The above samples send an unauthenticated `}<a parentName="p" {...{
        "href": "https://developer.seek.com/schema/#/query/version"
      }}><inlineCode parentName="a">{`version`}</inlineCode></a>{` query.
A valid access token is required in the `}<inlineCode parentName="p">{`Authorization`}</inlineCode>{` request header for other operations,
as covered in the previous `}<a parentName="p" {...{
        "href": "/auth#client-credentials"
      }}><PageIcon icon="Auth" mdxType="PageIcon" />{` Auth`}</a>{` section.`}</p>
    <h2 {...{
      "id": "code-samples"
    }}>{`Code samples`}</h2>
    <p>{`We provide GraphQL code samples across the Developer Site to demonstrate the implementation of features that are described in the documentation:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-scoobie-merged-code",
        "metastring": "[{\"type\":\"code\",\"lang\":\"graphql\",\"meta\":null,\"value\":\"query ($id: String!) {\\n  hiringOrganization(id: $id) {\\n    name\\n  }\\n}\",\"position\":{\"start\":{\"line\":481,\"column\":1,\"offset\":17418},\"end\":{\"line\":487,\"column\":4,\"offset\":17502},\"indent\":[1,1,1,1,1,1]}},{\"type\":\"code\",\"lang\":\"json\",\"meta\":null,\"value\":\"{\\n  \\\"id\\\": \\\"seekAnzPublicTest:organization:seek:93WyyF1h\\\"\\n}\",\"position\":{\"start\":{\"line\":489,\"column\":1,\"offset\":17504},\"end\":{\"line\":493,\"column\":4,\"offset\":17574},\"indent\":[1,1,1,1]}},{\"type\":\"code\",\"lang\":\"json\",\"meta\":null,\"value\":\"{\\n  \\\"hiringOrganization\\\": {\\n    \\\"name\\\": \\\"Acme Corp\\\"\\n  }\\n}\",\"position\":{\"start\":{\"line\":495,\"column\":1,\"offset\":17576},\"end\":{\"line\":501,\"column\":4,\"offset\":17645},\"indent\":[1,1,1,1,1,1]}}]",
        "[{\"type\":\"code\",\"lang\":\"graphql\",\"meta\":null,\"value\":\"query": true,
        "($id:": true,
        "String!)": true,
        "{\\n": true,
        "": true,
        "hiringOrganization(id:": true,
        "$id)": true,
        "name\\n": true,
        "}\\n}\",\"position\":{\"start\":{\"line\":481,\"column\":1,\"offset\":17418},\"end\":{\"line\":487,\"column\":4,\"offset\":17502},\"indent\":[1,1,1,1,1,1]}},{\"type\":\"code\",\"lang\":\"json\",\"meta\":null,\"value\":\"{\\n": true,
        "\\\"id\\\":": true,
        "\\\"seekAnzPublicTest:organization:seek:93WyyF1h\\\"\\n}\",\"position\":{\"start\":{\"line\":489,\"column\":1,\"offset\":17504},\"end\":{\"line\":493,\"column\":4,\"offset\":17574},\"indent\":[1,1,1,1]}},{\"type\":\"code\",\"lang\":\"json\",\"meta\":null,\"value\":\"{\\n": true,
        "\\\"hiringOrganization\\\":": true,
        "\\\"name\\\":": true,
        "\\\"Acme": true,
        "Corp\\\"\\n": true,
        "}\\n}\",\"position\":{\"start\":{\"line\":495,\"column\":1,\"offset\":17576},\"end\":{\"line\":501,\"column\":4,\"offset\":17645},\"indent\":[1,1,1,1,1,1]}}]": true
      }}>{`query ($id: String!) {
  hiringOrganization(id: $id) {
    name
  }
}
`}</code></pre>
    <p>{`There are a few aspects to each code sample:`}</p>
    <ol>
      <li parentName="ol">
        <p parentName="li">{`The first tab displays the query or mutation `}<a parentName="p" {...{
            "href": "/graphql/in-practice#operations"
          }}>{`operation`}</a>{` that is being described.`}</p>
      </li>
      <li parentName="ol">
        <p parentName="li">{`The second tab displays the input `}<a parentName="p" {...{
            "href": "/graphql/in-practice#variables"
          }}>{`variables`}</a>{` that are supplied alongside the operation.`}</p>
      </li>
      <li parentName="ol">
        <p parentName="li">{`The third tab displays the result of executing the operation with the supplied variables.`}</p>
      </li>
      <li parentName="ol">
        <p parentName="li">{`The Copy button copies the contents of the current tab to your clipboard.`}</p>
        <p parentName="li">{`This may be useful to incorporate the sample code into your software or an API testing tool.`}</p>
      </li>
      <li parentName="ol">
        <p parentName="li">{`The GraphQL Explorer button takes you to a `}<a parentName="p" {...{
            "href": "#dashboard"
          }}>{`dashboard`}</a>{` page where you can execute the operation.`}</p>
        <p parentName="li">{`The dashboard includes a lightweight, in-browser editor where you can execute GraphQL operations in one click.
It is similar in usage to API testing tools like Postman and Insomnia.`}</p>
        <p parentName="li">{`Our code samples reference `}<a parentName="p" {...{
            "href": "/graphql/playground/mock-objects"
          }}>{`mock objects`}</a>{` in our read-only `}<a parentName="p" {...{
            "href": "/graphql/playground"
          }}>{`Playground`}</a>{` environment.
This allows us to document operations that can be executed by all partners;
by contrast, access to production objects is typically restricted to a single hirer or partner.`}</p>
      </li>
    </ol>
    <h2 {...{
      "id": "dashboard"
    }}>{`Dashboard`}</h2>
    <p>{`The Developer Dashboard is an administrative user interface that lets you observe and configure your SEEK API integration:`}</p>

    <Actions mdxType="Actions">
  <ButtonLink href="/manage" icon={<IconArrow direction={'right'} mdxType="IconArrow" />} variant="soft" mdxType="ButtonLink">
    Dashboard
  </ButtonLink>
    </Actions>
    <p>{`It’s a key component of our `}<a parentName="p" {...{
        "href": "/introduction#integration-process"
      }}>{`integration process`}</a>{` and can be used to:`}</p>
    <ul>
      <li parentName="ul">
        <p parentName="li">{`Interact with `}<a parentName="p" {...{
            "href": "/use-cases/job-posting"
          }}>{`Job Posting`}</a>{` feature references`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li">{`Manage your `}<a parentName="p" {...{
            "href": "/events/webhooks"
          }}>{`webhook subscriptions`}</a></p>
      </li>
      <li parentName="ul">
        <p parentName="li">{`Configure notification channels for release notes and system health`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li">{`Create and rotate your `}<a parentName="p" {...{
            "href": "/auth#client-credentials"
          }}>{`client credentials`}</a></p>
      </li>
      <li parentName="ul">
        <p parentName="li">{`Run `}<a parentName="p" {...{
            "href": "/graphql/playground"
          }}>{`Playground`}</a>{` operations in the GraphQL Explorer`}</p>
      </li>
    </ul>
    <h2 {...{
      "id": "schema"
    }}>{`Schema`}</h2>
    <p>{`The schema documentation website lets you inspect low-level details of our GraphQL operations & types:`}</p>
    <Actions mdxType="Actions">
  <ButtonLink href="https://developer.seek.com/schema" icon={<IconNewWindow mdxType="IconNewWindow" />} variant="soft" mdxType="ButtonLink">
    Schema
  </ButtonLink>
    </Actions>
    <p>{`This documentation is automatically generated from our GraphQL schema.
You can `}<a parentName="p" {...{
        "href": "https://graphql.org/learn/introspection/"
      }}>{`introspect`}</a>{` the schema yourself by pointing GraphQL client tooling at `}<inlineCode parentName="p">{`https://graphql.seek.com/graphql`}</inlineCode>{`.
See `}<a parentName="p" {...{
        "href": "/graphql/in-practice"
      }}>{`GraphQL in practice`}</a>{` for more information about our GraphQL endpoint and client tooling.`}</p>
    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;